/**
 * Inscription du service worker sans plugin ni dépendance externe (OPS-013).
 *
 * Le cache reçoit uniquement les ressources publiques déjà chargées par Vite.
 * Les URL API et plugin sont filtrées une seconde fois dans le service worker.
 */
/**
 * Recharge la page UNE fois quand un nouveau service worker prend la main.
 *
 * Sans cela, un navigateur ayant déjà visité le site continue d'exécuter l'ancien paquet
 * jusqu'à ce que tous ses onglets soient fermés. Le 10/09/2026, c'est ce qui a bloqué la
 * connexion pendant des heures : la coquille en cache datait d'avant la correction de la
 * boucle OIDC, `/callback` y était encore derrière la garde d'authentification, et chaque
 * retour de Keycloak réaffichait l'écran de connexion. Le serveur servait le bon code, le
 * navigateur en exécutait un autre, et rien ne le disait.
 *
 * Un déploiement doit atteindre les gens qui utilisent déjà l'application, pas seulement
 * ceux qui arrivent après.
 */
function rechargerQuandLeWorkerChange(): void {
  let dejaRecharge = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    // Le garde-fou est indispensable : sans lui, un worker qui reprend la main pendant le
    // rechargement en déclenche un autre, et la page boucle.
    if (dejaRecharge) return;
    dejaRecharge = true;
    window.location.reload();
  });
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!import.meta.env.PROD || !("serviceWorker" in navigator)) {
    return null;
  }

  try {
    rechargerQuandLeWorkerChange();

    const registration = await navigator.serviceWorker.register("/sw.js", { scope: "/" });

    // Demander tout de suite au navigateur s'il existe une version plus récente : livré à
    // lui-même, il peut attendre vingt-quatre heures avant de regarder.
    registration.update().catch(() => {
      /* hors ligne : on garde ce qu'on a, c'est précisément le rôle du service worker */
    });

    await navigator.serviceWorker.ready;

    const urls = new Set<string>(["/index.html", "/manifest.json", "/icon.svg"]);
    for (const entry of performance.getEntriesByType("resource")) {
      const url = new URL(entry.name, window.location.origin);
      if (url.origin === window.location.origin && url.pathname.startsWith("/assets/")) {
        urls.add(url.pathname + url.search);
      }
    }

    const worker = registration.active ?? registration.waiting ?? registration.installing;
    worker?.postMessage({ type: "CACHE_URLS", urls: [...urls] });
    return registration;
  } catch (error) {
    // L'application reste utilisable en ligne si le navigateur refuse le service worker.
    console.warn("Service worker InfraNet GN indisponible", error);
    return null;
  }
}
